import { isEmpty, cloneDeep } from 'lodash-es';
import { CountryCode, getCountryCallingCode, parsePhoneNumber } from 'libphonenumber-js';
import { replaceNonDigits, parseRegex, removeEmpty } from 'utilities/global';
import { IFieldMetadata } from 'models';
import { ICustomerData } from 'models/customer';
import { ERROR_MESSAGES } from 'consts/tokenization';
import { CUSTOMER_FIELDS } from 'consts/customer';

export const CUSTOMER_ADDRESS_FIELDS_REQUIRED_MAPPING = (withState = true) => ({
  line_1: {
    name: 'line_1',
    type: 'string',
    description: 'Street name, building number and apartment number',
    required: true
  },
  city: {
    name: 'city',
    type: 'string',
    description: 'Customer’s city',
    required: true
  },
  zip: {
    name: 'zip',
    type: 'string',
    description: 'Customer’s postal code',
    required: true
  },
  country: {
    name: 'country',
    type: 'string',
    description: 'Customer’s country',
    required: true
  },
  ...(withState && {
    state: {
      name: 'state',
      type: 'string',
      regex: '^.{1,40}',
      description: 'Customer’s state',
      is_required: true
    }
  })
});

export const constructE164 = ({
  countryCode,
  number,
  withSign
}: {
  countryCode: CountryCode;
  number: string;
  withSign?: boolean;
}) => {
  if (!countryCode || !number) return '';
  return `${withSign ? '+' : ''}${replaceNonDigits(
    `${getCountryCallingCode(countryCode)} ${number}`
  )}`;
};

export const parseRequiredFieldsFromServerToRequiredMapping = ({
  fields,
  flag = 'u'
}: {
  fields: Record<string, IFieldMetadata | Record<string, IFieldMetadata>>;
  flag?: string;
}) => {
  const requiredMapping = {} as Record<string, any>;

  if (!fields) return {};

  Object.entries(fields).forEach(([key, field]) => {
    if (key === CUSTOMER_FIELDS.addresses) {
      Object.assign(
        requiredMapping,
        parseRequiredFieldsFromServerToRequiredMapping({
          fields: field as Record<string, IFieldMetadata>,
          flag
        })
      );
    } else {
      requiredMapping[key] = {
        required: field.is_required,
        hidden: false,
        description: field.description,
        ...(field.regex
          ? {
              validation: (val: any, mobileFlag = 'u') =>
                parseRegex(field.regex as string, flag || mobileFlag)?.test(val)
                  ? undefined
                  : ERROR_MESSAGES.invalid_field,
              regex: field.regex
            }
          : {})
      };
    }
  });

  return requiredMapping;
};

export const initiateCustomerFields = (defaultValue: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(CUSTOMER_FIELDS).map(([_, value]) => [value, defaultValue])
  );

export const parseCustomerToRequest = ({
  customerData,
  values
}: {
  customerData: ICustomerData;
  values: Record<string, any>;
}) => {
  if (isEmpty(values)) return;
  const cleanValues = removeEmpty(values);
  const customer = cloneDeep(cleanValues);

  if (!isEmpty(customer[CUSTOMER_FIELDS.addresses])) {
    customer[CUSTOMER_FIELDS.addresses][CUSTOMER_FIELDS.name] =
      customer[CUSTOMER_FIELDS.addresses][CUSTOMER_FIELDS.name] ||
      customerData?.name ||
      customer[CUSTOMER_FIELDS.name] ||
      'customer_address';

    const { number, formattedNumber } =
      customer[CUSTOMER_FIELDS.addresses]?.[CUSTOMER_FIELDS.phone] || {};
    if (number) {
      customer[CUSTOMER_FIELDS.addresses][CUSTOMER_FIELDS.phone] = formattedNumber;
    }
    customer[CUSTOMER_FIELDS.addresses] = [customer[CUSTOMER_FIELDS.addresses]];
  }

  const { number, formattedNumber } = customer[CUSTOMER_FIELDS.phone] || {};
  if (number) {
    customer[CUSTOMER_FIELDS.phone] = formattedNumber;
  } else {
    customer[CUSTOMER_FIELDS.phone] = customerData?.phone_number;
  }

  return customer;
};

export const parseE164ToPhoneNumber = (e164: string) => {
  try {
    const result = parsePhoneNumber(e164);
    return {
      countryCode: result?.country,
      number: result?.nationalNumber
    };
  } catch (err) {}
};
